import { computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { EntitySelectorRequestModel } from '@core/models/entity/entity-selector-request.model';
import { SelectorsService } from '@core/services/selectors/selectors.service';
import {
  selectAvailableEntities,
  selectDataspaceState
} from '@core/store/dataspace/dataspace.selectors';

/** Ceiling on rows fetched for one backdrop, paged under the API's per-page maximum. */
export const BACKDROP_ROW_LIMIT = 2000;

/**
 * A layer of context drawn underneath the entities a map is actually about — sites beneath
 * geographic zones, zones beneath sites and routes.
 *
 * Loading is always explicit. A backdrop costs a paged fetch of up to
 * {@link BACKDROP_ROW_LIMIT} rows, so nothing here is pulled until asked for, and what is
 * loaded outlives whatever prompted it: a drawing session ending must not throw it away.
 *
 * Rows belong to the dataspace they were fetched from. Staleness is derived from that
 * rather than reset on switch, so a dataspace change cannot leave another dataspace's rows
 * on the map through a reset someone forgot to wire.
 */
export abstract class MapBackdropService<TRow, TItem> {
  private readonly store = inject(Store);
  private readonly selectorsService = inject(SelectorsService);
  private readonly dataspace = this.store.selectSignal(selectDataspaceState);
  private readonly availableEntities = this.store.selectSignal(selectAvailableEntities);

  private readonly itemsState = signal<TItem[]>([]);
  private readonly availableState = signal(0);
  private readonly isLoadingState = signal(false);
  private readonly isShownState = signal(false);
  private readonly loadedForDataspace = signal<number | null>(null);

  private readonly dataspaceId = computed(() => this.dataspace().dataspace?.id ?? null);

  private readonly isFresh = computed(
    () =>
      this.loadedForDataspace() !== null && this.loadedForDataspace() === this.dataspaceId()
  );

  readonly items = computed(() => (this.isFresh() ? this.itemsState() : []));
  /** How many rows the dataspace holds, which may exceed what the cap allowed us to load. */
  readonly available = computed(() => (this.isFresh() ? this.availableState() : 0));
  readonly isLoading = this.isLoadingState.asReadonly();
  readonly isShown = computed(() => this.isFresh() && this.isShownState());
  readonly displayedItems = computed(() => (this.isShown() ? this.items() : []));

  /** GBE entity type to query, e.g. `SITE`. */
  protected abstract readonly entityType: string;
  /**
   * The entity's name in the dataspace catalogue, compared on letters alone so
   * `geographic_area`, `GeographicArea` and `geographic area` all match.
   */
  protected abstract readonly entityName: string;
  protected abstract build(rows: TRow[]): TItem[];

  /** Loads the backdrop on first use, then flips between shown and hidden. */
  toggle(): void {
    if (this.isLoading()) {
      return;
    }
    if (!this.isFresh()) {
      this.load();
      return;
    }
    this.isShownState.update((shown) => !shown);
  }

  private load(): void {
    const dataSpaceId = this.dataspaceId();
    const versionName = this.resolveVersionName();

    if (!dataSpaceId || !versionName) {
      return;
    }

    const request: EntitySelectorRequestModel = {
      entityType: this.entityType,
      entityTypeVersion: versionName,
      dataSpaceId
    };

    this.isLoadingState.set(true);

    this.selectorsService
      .selectDataUpTo$<TRow>(request, BACKDROP_ROW_LIMIT)
      .pipe(take(1))
      .subscribe({
        next: (page) => {
          this.itemsState.set(this.build(page.items));
          this.availableState.set(page.available);
          this.loadedForDataspace.set(dataSpaceId);
          this.isShownState.set(true);
          this.isLoadingState.set(false);
        },
        error: () => {
          this.isLoadingState.set(false);
        }
      });
  }

  private resolveVersionName(): string | undefined {
    const wanted = lettersOf(this.entityName);
    return this.availableEntities().find(
      (entity) => lettersOf(entity.name ?? '') === wanted
    )?.versions[0]?.versionName;
  }
}

function lettersOf(value: string): string {
  return value.toLowerCase().replace(/[^a-z]/g, '');
}
